import { AxiosResponse } from "axios";
import { axiosInstance, MessageResponse } from "./common";

export enum SanctionEventType {
    FRESH_SANCTION = "FRESH_SANCTION",
    TAKEOVER_SANCTION = "TAKEOVER_SANCTION",
    ENHANCEMENT = "ENHANCEMENT",
    RENEW_AND_ENHANCEMENT = "RENEW_AND_ENHANCEMENT",
    REVIEW_OR_RENEWAL = "REVIEW_OR_RENEWAL",
    OPERATIONAL_REVIEW = "OPERATIONAL_REVIEW",
    RENEWAL_WITH_REDUCTION = "RENEWAL_WITH_REDUCTION",
    PARTIAL_TAKEOVER_AND_ENHANCEMENT = "PARTIAL_TAKEOVER_AND_ENHANCEMENT",
    SANCTION_WITH_SUB_LIMIT_ALLOCATION = "SANCTION_WITH_SUB_LIMIT_ALLOCATION",
    RESTRENGTHENING_AND_RESTRUCTURE = "RESTRENGTHENING_AND_RESTRUCTURE",
}

export interface SanctionEventMetadata {
    event_id: string;
    authority_name: string;
    event_type: SanctionEventType;
    sanction_date: string;
}

export interface SanctionEventDetail {
    detail_id: string;
    loan_account_number: string;
    sanction_amount: number;
}

export interface SanctionEventEntity extends SanctionEventDetail {
    event_id: string;
}

export interface SanctionEvent extends SanctionEventMetadata {
    details: SanctionEventDetail[];
}

export interface GetSanctionEventsResponse {
    events_metadata: SanctionEventMetadata[];
    events_details: SanctionEventEntity[];
}

export interface UpsertSanctionEventsRequest {
    events_metadata?: SanctionEventMetadata[] | null;
    events_details?: SanctionEventEntity[] | null;
}

export interface GetSanctionEventsParams {
    case_id: string;
}

export interface UpsertSanctionEventsParams {
    case_id: string;
    request: UpsertSanctionEventsRequest;
}

const toSanctionEventsWriteRequest = (
    request: UpsertSanctionEventsRequest,
): UpsertSanctionEventsRequest => {
    const writeRequest: UpsertSanctionEventsRequest = {};

    if (request.events_metadata !== undefined) {
      writeRequest.events_metadata =
        request.events_metadata?.map(
          ({ event_id, authority_name, event_type, sanction_date }) => ({
            event_id,
            authority_name,
            event_type,
            sanction_date,
          }),
        ) ?? null;
    }

    if (request.events_details !== undefined) {
      writeRequest.events_details =
        request.events_details?.map(
          ({ event_id, detail_id, loan_account_number, sanction_amount }) => ({
            event_id,
            detail_id,
            loan_account_number,
            sanction_amount,
          }),
        ) ?? null;
    }

    return writeRequest;
};

const SanctionEventsAPI = {
    getSanctionEvents: async (params: GetSanctionEventsParams) => {
      const response: AxiosResponse<GetSanctionEventsResponse> =
        await axiosInstance.get(`/v1/cases/${params.case_id}/sanction-events`);

      return response.data;
    },

    upsertSanctionEvents: async (params: UpsertSanctionEventsParams) => {
      const response: AxiosResponse<MessageResponse> = await axiosInstance.put(
        `/v1/cases/${params.case_id}/sanction-events`,
        toSanctionEventsWriteRequest(params.request),
      );

      return response.data;
    },
};

export default SanctionEventsAPI;
